<template>
  <ClientOnly>
    <div v-if="cartItems.length > 0" class="basket-recommendations">
      <div class="basket-recommendations__title">Рекомендуем также</div>
      
      <!-- Индикатор загрузки -->
      <div v-if="isLoading" class="basket-recommendations__loading">
        <div class="loading-spinner"></div>
        <span>Загружаем рекомендации...</span>
      </div>
      
      <!-- Список рекомендаций -->
      <swiper-container
        v-else-if="recommendedProducts.length"
        ref="swiperContainer"
        class="slider basket-recommendations-slider"
        events-prefix="basket-recommendations-slider-"
        space-between="16"
        :breakpoints="{
          320: {
            slidesPerView: 2.3,
            spaceBetween: 12,
          }
        }"
      >
        <swiper-slide
          v-for="(entry, index) in recommendationEntries"
          :key="entry.item.id ?? index"
          class="slide"
        >
          <div
            class="card"
            @click="openProduct(entry.item)"
          >
            <div class="image-container">
              <video
                v-if="entry.media.isVideo"
                :src="entry.media.url"
                playsinline
                muted
                loop
                autoplay
                preload="metadata"
                class="image"
                :aria-label="entry.item.name"
              />
              <img
                v-else
                :src="entry.media.url"
                :alt="entry.item.name"
                loading="lazy"
                class="image"
                @error="handleImageError"
              />
            </div>
            <div class="content">
              <div class="name">{{ entry.item.name }}</div>
              <div class="price">{{ entry.item.price }} ₽</div>
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { consola } from 'consola'
import { useInstitutionStore } from '@entities/institution'
import { useCartStore } from '@entities/order'
import { useModalStore, modalStateEnum } from '@shared/model'
import { apiRecommendation } from '~/entities/product'
import { isVideoFile, setProductOpenSource } from '@shared/lib'

interface Props {
  cartItems: Array<{ id: number }>
}

const props = defineProps<Props>()

const cartStore = useCartStore()
const storeInstitution = useInstitutionStore()
const storeModal = useModalStore()

interface RecommendedProduct {
  id: string
  name: string
  price: string
  img: string[]
  is_favorite: boolean
  category_id: string
}

const recommendedProducts = ref<RecommendedProduct[]>([])
const isLoading = ref(false)
const swiperContainer = ref<any>(null)

/**
 * Плейсхолдер для изображения
 */
const placeholderImg = computed(() => storeInstitution.institution?.visual?.placeholder_file)

/**
 * Первая валидная ссылка на медиа (как в каталоге: объекты с url или строки)
 */
const firstMediaUrl = (images: any[]): string => {
  if (!Array.isArray(images) || images.length === 0) {
    return placeholderImg.value ?? ''
  }

  for (const image of images) {
    if (image && typeof image === 'object' && image.url) {
      return image.url
    }
    if (typeof image === 'string') {
      return image
    }
  }

  return placeholderImg.value ?? ''
}

type RecommendationEntry = {
  item: RecommendedProduct
  media: { url: string; isVideo: boolean }
}

const recommendationEntries = computed<RecommendationEntry[]>(() =>
  recommendedProducts.value.map((item) => {
    const url = firstMediaUrl(item.img)
    return {
      item,
      media: { url, isVideo: isVideoFile(url) },
    }
  }),
)

/**
 * Обработчик ошибки загрузки изображения
 */
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  if (!img.src.includes('placeholder')) {
    img.src = placeholderImg.value
  }
}

/**
 * Получает рекомендации на основе всех товаров в корзине.
 * API v2 возвращает готовые объекты товаров — дополнительный запрос каталога не нужен.
 */
const getBasketRecommendations = async (): Promise<void> => {
  try {
    isLoading.value = true

    const branchId = storeInstitution.selectedBranch?.id

    if (!branchId) {
      recommendedProducts.value = []
      return
    }

    const itemIds = props.cartItems.map(item => item.id)

    if (itemIds.length === 0) {
      recommendedProducts.value = []
      return
    }

    // API возвращает полные объекты товаров: GET /recommendations?items=...&branch_id=...&limit=10
    const products = await apiRecommendation.getBasketRecommendations(itemIds, branchId, 10)

    recommendedProducts.value = products ?? []
  } catch (error) {
    consola.error('Ошибка получения рекомендаций для корзины:', error)
    recommendedProducts.value = []
  } finally {
    isLoading.value = false
  }
}

/**
 * Ищет полный объект товара в каталоге по id (с описанием/вариантами/модификаторами).
 * Рекомендации могут прийти как id ВАРИАНТА группового товара — тогда возвращаем
 * родительскую группу и id варианта для предвыбора (иначе модалка открывалась с «голым»
 * объектом без добавок и ломала кол-во → 400 «quantity must not be less than 0.001»).
 */
const findCatalogProduct = (id: string | number): { product: any; variantId: number | string | null } | null => {
  const sid = String(id)
  const categories: any[] = (storeInstitution.products as any[]) ?? []
  for (const category of categories) {
    for (const product of (category?.products ?? [])) {
      // Прямое совпадение (single / group / combo)
      if (String(product?.id) === sid) return { product, variantId: null }
      // Совпадение по варианту внутри группы → открываем группу, предвыбираем вариант
      if (product?.type === 'group' && Array.isArray(product.variants)) {
        const variant = product.variants.find((v: any) => String(v?.id) === sid)
        if (variant) return { product, variantId: variant.id }
      }
    }
  }
  return null
}

/**
 * Открывает карточку товара (как в каталоге) вместо мгновенного добавления —
 * пользователь сам выбирает кол-во/модификаторы.
 */
const openProduct = (item: RecommendedProduct) => {
  setProductOpenSource('recommendation')
  const match = findCatalogProduct(item.id)
  if (match) {
    cartStore.setSelectedDish(match.product, match.variantId)
  } else {
    // В каталоге не нашли (редко) — открываем то, что пришло в рекомендации
    cartStore.setSelectedDish(item as any)
  }
  storeModal.open(modalStateEnum.MoreAboutDishRefactored)
}

// Отслеживаем изменения в корзине
watch(() => props.cartItems, () => {
  getBasketRecommendations()
}, { immediate: true, deep: true })
</script>

<style lang="postcss">
.basket-recommendations {
  @apply w-full pb-6 relative;

  &__title {
    @apply mb-4 text-xl font-bold text-gray-900 dark:text-white tracking-tight;
  }

  &__loading {
    @apply flex items-center justify-center py-8 text-gray-500;
    
    .loading-spinner {
      @apply w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2;
    }
  }

  .slider {
    @apply w-full px-2 py-2;

    .slide {
      @apply h-auto;

      .card {
        @apply relative flex flex-col h-full cursor-pointer items-center;
        background: linear-gradient(135deg, rgba(229, 231, 235, 1), rgba(229, 231, 235, 0.5));
        /* Скругление из пресета «Скругления элементов» */
        border-radius: var(--app-radius-card, 24px);
        padding: 1rem;
        box-shadow:
          0 1px 3px rgba(0, 0, 0, 0.05),
          0 10px 15px -3px rgba(0, 0, 0, 0.05);
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid rgba(0, 0, 0, 0.06);
        overflow: hidden;

        /* Микровзаимодействия для обратной связи */
        &:hover {
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.08),
            0 20px 25px -5px rgba(0, 0, 0, 0.08);
          border-color: rgba(59, 130, 246, 0.3);
        }

        &:active {
          transition-duration: 0.15s;
        }

        .image-container {
          @apply relative overflow-hidden mb-3 w-full;
          border-radius: var(--app-radius-media, 16px);
          padding-top: 100%;
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.05));
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);

          .image {
            @apply absolute top-0 left-0 w-full h-full object-cover;
            transition: transform 0.4s ease;
            pointer-events: none;
          }
        }

        &:hover .image {
          transform: scale(1.05);
        }

        .content {
          @apply flex flex-col flex-grow w-full;

          .name {
            @apply text-sm font-semibold text-gray-900 dark:text-white mb-2 tracking-tight;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.3;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .price {
            @apply text-lg font-bold text-gray-900 dark:text-white mt-auto tracking-tight;
            font-variant-numeric: tabular-nums;
          }
        }

        @media (max-width: 768px) {
          border-radius: calc(var(--app-radius-card, 24px) * 0.7);
          padding: 0.75rem;
          box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.05),
            0 8px 12px -3px rgba(0, 0, 0, 0.05);

          .content .name {
            @apply text-xs;
            line-height: 1.25;
          }

          .content .price {
            @apply text-base;
          }
        }
      }
    }
  }
}

/* Адаптация для мобильных устройств */
@media (max-width: 767px) {
  .basket-recommendations .slider {
    @apply px-0;
  }
}

/* Темная тема */
.dark .basket-recommendations {
  .slider .slide .card {
    background: linear-gradient(135deg, rgba(24, 24, 27, 1), rgba(24, 24, 27, 0.5));
    border-color: rgba(255, 255, 255, 0.1);

    &:hover {
      border-color: rgba(59, 130, 246, 0.4);
    }

    .image-container {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01));
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
    }
  }
}

/* ============================================ */
/* ♿ ACCESSIBILITY - Доступность */
/* ============================================ */

/* Сенсорные устройства */
@media (hover: none) and (pointer: coarse) {
  .basket-recommendations .slider .slide .card {
    &:hover {
      transform: none;
    }

    &:hover .image {
      transform: none;
    }
  }
}

/* Высокий контраст */
@media (prefers-contrast: high) {
  .basket-recommendations .slider .slide .card {
    border-width: 2px;
  }
}

/* Уменьшенная анимация */
@media (prefers-reduced-motion: reduce) {
  .basket-recommendations .slider .slide .card,
  .basket-recommendations .slider .slide .card .image {
    transition: none;
  }

  .basket-recommendations .slider .slide .card:hover .image {
    transform: none;
  }
}
</style>

